import React from 'react';
import { Form, Select, Spin, Empty, Tooltip } from 'antd';
import { Field, FieldProps, FastField } from 'formik';
import { Controller, useFormContext } from 'react-hook-form';
import { get as _get } from 'lodash';
import { SelectProps, SelectValue } from 'antd/lib/select';

type FormSelectProps = {
  optionToolTip?: boolean;
  name: string;
  className?: string;
  values: {
    id: string | number | undefined;
    name: string | number;
    code?: string;
    path?: string;
    disabled?: boolean;
  }[];
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  useCode?: boolean;
  addPathToOption?: boolean;
  testId?: string;
  noStyle?: boolean;
  clearContainer?: boolean;
  fastField?: boolean;
  searchId?: boolean;
  onChange?: (value: SelectValue) => void;
  handleOnBlur?: () => void;
  useHookForm?: boolean;
} & SelectProps<any>;

const FormSelect: React.FC<FormSelectProps> = props => {
  const {
    name,
    className,
    values,
    label,
    useCode,
    addPathToOption,
    required,
    noStyle,
    onChange,
    testId,
    clearContainer,
    fastField,
    optionToolTip,
    searchId,
    handleOnBlur,
    useHookForm,
    ...selectProps
  } = props;

  const { control } = useFormContext() || {};

  if (useHookForm) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Form.Item
            className={className}
            label={label}
            required={required}
            noStyle={noStyle}
            hasFeedback
            validateStatus={error ? 'error' : ''}
            help={error?.message || undefined}
          >
            <Select
              {...field}
              value={field.value || (field.value === null ? undefined : field.value)}
              data-testid={testId}
              onChange={value =>
                onChange ? onChange(value) : field.onChange(value === undefined ? null : value)
              }
              onBlur={() => {
                field.onBlur();
                if (handleOnBlur) handleOnBlur();
              }}
              filterOption={
                props.onSearch
                  ? false
                  : (inputValue, option) =>
                      !optionToolTip
                        ? option?.label &&
                          (option?.label.props.children[0]
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()) ||
                            (searchId && option?.value?.toString().includes(inputValue)))
                        : option?.label &&
                          option?.label.props.children[0].props.title
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
              }
              getPopupContainer={clearContainer ? undefined : trigger => trigger.parentNode}
              notFoundContent={
                props.loading ? <Spin /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              }
              {...selectProps}
              options={values.map(value => ({
                key: value.id,
                value: useCode ? value.code! : value.id!,
                disabled: value.disabled,
                label: (
                  <React.Fragment>
                    {!optionToolTip ? (
                      value.name
                    ) : (
                      <Tooltip title={value.name}>
                        <span>{value.name} </span>
                      </Tooltip>
                    )}
                    {addPathToOption && (
                      <span style={{ fontSize: '9px' }}>{` (${value.path})`}</span>
                    )}
                  </React.Fragment>
                ),
              }))}
            />
          </Form.Item>
        )}
      />
    );
  }

  const fieldContent = ({ field, form }: FieldProps) => {
    const { errors, touched, setFieldValue, setFieldTouched } = form;
    const showValidationInfo = !!(_get(errors, name) && _get(touched, name));
    return (
      <Form.Item
        className={className}
        label={label}
        required={required}
        noStyle={noStyle}
        hasFeedback
        validateStatus={showValidationInfo ? 'error' : ''}
        help={(showValidationInfo && _get(errors, name)?.toString()) || undefined}
      >
        <Select
          data-testid={testId}
          value={field.value || (field.value === null ? undefined : field.value)}
          onChange={value =>
            onChange ? onChange(value) : setFieldValue(name, value === undefined ? null : value)
          }
          onBlur={() => {
            setFieldTouched(name);
            if (handleOnBlur) handleOnBlur();
          }}
          filterOption={
            props.onSearch
              ? false
              : (inputValue, option) =>
                  !optionToolTip
                    ? option?.label &&
                      (option?.label.props.children[0]
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                        (searchId && option?.value?.toString().includes(inputValue)))
                    : option?.label &&
                      option?.label.props.children[0].props.title
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
          }
          getPopupContainer={clearContainer ? undefined : trigger => trigger.parentNode}
          notFoundContent={
            props.loading ? <Spin /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          }
          {...selectProps}
          options={values.map(value => ({
            key: value.id,
            value: useCode ? value.code! : value.id!,
            disabled: value.disabled,
            label: (
              <React.Fragment>
                {!optionToolTip ? (
                  value.name
                ) : (
                  <Tooltip title={value.name}>
                    <span>{value.name} </span>
                  </Tooltip>
                )}
                {addPathToOption && <span style={{ fontSize: '9px' }}>{` (${value.path})`}</span>}
              </React.Fragment>
            ),
          }))}
        />
      </Form.Item>
    );
  };

  return fastField ? (
    <FastField name={name}>{fieldContent}</FastField>
  ) : (
    <Field name={name}>{fieldContent}</Field>
  );
};

export default FormSelect;
